import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { X } from 'lucide-react';

/**
 * The app's pop-up: serif title, close cross, scrolling body over a blurred
 * backdrop. One shell for the edit forms, the Day Planner's spot editor and
 * the traveller dialogs, which had each grown their own padding and colour.
 * Portalled into #import-modal-root (see talisman_modals memory: a fixed
 * element inside a transformed ancestor is trapped on iOS PWAs).
 */
export function Modal({ title, onClose, children, size = 'xl' }: {
  title: ReactNode; onClose: () => void; children: ReactNode; size?: 'md' | 'xl';
}) {
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={onClose}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-app-bg/80 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={e => e.stopPropagation()}
        className={`bg-app-bg border border-app-border rounded-3xl p-6 w-full ${size === 'md' ? 'max-w-md' : 'max-w-xl'} max-h-[85vh] overflow-y-auto overflow-x-hidden shadow-2xl`}
      >
        <div className="flex justify-between items-start gap-3 mb-5">
          <h3 className="font-serif text-2xl font-bold text-app-text text-balance leading-tight">{title}</h3>
          <button aria-label="Close" onClick={onClose} className="p-2 -mr-2 -mt-1 text-app-sub/40 hover:text-gold transition-colors shrink-0">
            <X className="w-6 h-6" />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>,
    document.getElementById('import-modal-root') ?? document.body,
  );
}
